import type { Status } from '@/components/status-badge';

// Written by prewarm-db.sh on the devbox; absent on manifests from before it existed.
export interface PrewarmStatus {
  state: 'running' | 'done' | 'failed';
  message: string;
  started_at: string;
  updated_at: string;
}

export interface StagingManifestView {
  phase: string;
  built_sha: string;
  updated_at: string;
  frontend_url: string;
  prewarm?: PrewarmStatus | null;
}

export interface StagingView {
  manifest: StagingManifestView | null;
  error: string | null;
}

// A HEALTHY staging is usable before its restored database has finished
// hydrating; this tells testers why the first reports may be slow.
export function prewarmNote(manifest: StagingManifestView | null): string | null {
  const p = manifest?.prewarm;
  if (!p) return null;
  if (p.state === 'running') return `Database still warming up — ${p.message}`;
  if (p.state === 'failed') return 'Database warm-up failed — reports may run slow';
  return 'Database fully warm';
}

// Why the promote button is disabled — null means it's allowed. The workflow
// re-validates everything server-side; this only drives the UI.
export function promoteBlockedReason(staging: StagingView, prodSha: string | null): string | null {
  if (staging.error) return 'Staging state unavailable';
  if (!staging.manifest) return 'Deploy staging first';
  if (staging.manifest.phase === 'FAILED') return 'Staging deploy failed — redeploy it';
  if (staging.manifest.phase !== 'HEALTHY') return 'Staging is still deploying';
  if (prodSha && staging.manifest.built_sha === prodSha) return 'Already in production';
  return null;
}

export function shortSha(sha: string | null | undefined): string {
  return sha ? sha.slice(0, 8) : '—';
}

export function runStatusBadge(run: { status: string; conclusion: string | null } | null): {
  status: Status;
  label: string;
} {
  if (!run) return { status: 'unknown', label: 'Never run' };
  if (run.status !== 'completed') return { status: 'warning', label: 'In progress' };
  if (run.conclusion === 'success') return { status: 'healthy', label: 'Success' };
  if (run.conclusion === 'cancelled') return { status: 'unknown', label: 'Cancelled' };
  return { status: 'critical', label: run.conclusion ?? 'Failed' };
}

export type StepState = 'done' | 'active' | 'busy' | 'blocked' | 'failed';

export interface CycleStep {
  state: StepState;
  detail: string;
}

export interface ReleaseCycle {
  stage: CycleStep;
  test: CycleStep;
  ship: CycleStep;
}

// Maps raw release status onto the three-step cycle the page renders:
// Stage -> Test -> Ship. Exactly one step is ever 'active'/'busy'/'failed',
// so the page always has a single obvious next action. The server bridges the
// dispatch-to-run-visible gap by synthesizing a queued run, so an in-flight
// run here is the sole "something is happening" signal.
export function releaseCycle(
  staging: StagingView,
  prodSha: string | null,
  promoteRun: { status: string; conclusion: string | null } | null,
  deployRun: { status: string; conclusion: string | null } | null
): ReleaseCycle {
  const waitingRest = {
    test: { state: 'blocked', detail: 'waiting for staging' } as CycleStep,
    ship: { state: 'blocked', detail: 'waiting for staging' } as CycleStep,
  };

  if (promoteRun && promoteRun.status !== 'completed') {
    return {
      stage: { state: 'done', detail: `build ${shortSha(staging.manifest?.built_sha)}` },
      test: { state: 'done', detail: 'verified' },
      ship: { state: 'busy', detail: 'promotion running — deploys follow automatically' },
    };
  }
  const deployInFlight = deployRun !== null && deployRun.status !== 'completed';
  const m = staging.manifest;
  if (deployInFlight) {
    return {
      stage: {
        state: 'busy',
        detail:
          m && m.phase !== 'HEALTHY' && m.phase !== 'FAILED'
            ? `deploying — ${m.phase.toLowerCase().replaceAll('_', ' ')}`
            : m
              ? 'rebuild in progress — current staging is being replaced'
              : 'starting — teardown and rebuild take a few minutes to show',
      },
      ...waitingRest,
    };
  }
  if (staging.error) {
    return { stage: { state: 'blocked', detail: 'staging state unavailable' }, ...waitingRest };
  }
  if (!m) {
    const lastDeployFailed =
      deployRun?.status === 'completed' &&
      deployRun.conclusion !== 'success' &&
      deployRun.conclusion !== 'cancelled';
    return {
      stage: lastDeployFailed
        ? { state: 'failed', detail: 'last staging deploy failed — check the run, then retry' }
        : { state: 'active', detail: 'not running' },
      ...waitingRest,
    };
  }
  if (m.phase === 'FAILED') {
    return {
      stage: { state: 'failed', detail: 'staging deploy failed — rebuild it' },
      ...waitingRest,
    };
  }
  if (m.phase !== 'HEALTHY') {
    return {
      stage: { state: 'busy', detail: `deploying — ${m.phase.toLowerCase().replaceAll('_', ' ')}` },
      ...waitingRest,
    };
  }
  if (prodSha && m.built_sha === prodSha) {
    return {
      stage: { state: 'done', detail: `build ${shortSha(m.built_sha)} is live in production` },
      test: { state: 'done', detail: 'verified' },
      ship: {
        state: 'done',
        detail: 'production is up to date — deploy staging to start a new cycle',
      },
    };
  }
  return {
    stage: { state: 'done', detail: `build ${shortSha(m.built_sha)} ready` },
    test: { state: 'active', detail: 'verify the pending changes on staging' },
    ship: { state: 'active', detail: `promote build ${shortSha(m.built_sha)}` },
  };
}
